// ---------------------------------------------------------------------------
// SummaryStep — Review screen before completion
// ---------------------------------------------------------------------------
// Compact summary layout:
//   - Location card: country flag + address, consumer type below
//   - Tariffs card: merged subsections for Energy, DSO, Authorities
//
// Resolves display names from three sources in priority order:
//   1. display_values (backend-provided — best, no extra API calls)
//   2. optionsCache (populated during normal forward flow)
//   3. Raw ID fallback (last resort when neither source has the value)
// ---------------------------------------------------------------------------

import { memo, useCallback, useMemo, type ReactNode } from 'react';
import { StepHeading } from '@/components/shared/StepHeading';
import { FooterActions } from '@/components/shell/FooterActions';
import { t } from '@/i18n';
import { getStepIcon } from '@/utils/stepIcons';
import { countryFlagUrl } from '@/utils/format';
import { isSafeImageUrl } from '@/utils/url';
import type { StepId, StepOptionsResponse } from '@/api/types';
import { useSessionState, useSessionActions } from '@/hooks/useSession';
import { usePrefetchSummaryOptions } from '@/hooks/usePrefetchSummaryOptions';
import { CURRENCY_SYMBOLS, getPredefinedSet } from '@/utils/constants';
import { getRemovable, codeLabel, customTypeLabel } from '@/utils/stepOptions';
import type { StepProps } from '@/types/steps';
import styles from './SummaryStep.module.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Safely read a string from the untyped collected_data bag. Coerces numbers. */
function str(data: Record<string, unknown>, key: string): string {
  const v = data[key];
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return '';
}

/** Safely read a nested object. */
function obj(data: Record<string, unknown>, key: string): Record<string, unknown> {
  const v = data[key];
  return typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {};
}

/** Safely read a list of dict entries. */
function arr(data: Record<string, unknown>, key: string): Record<string, unknown>[] {
  const v = data[key];
  if (!Array.isArray(v)) return [];
  return v.filter(
    (e): e is Record<string, unknown> => typeof e === 'object' && e !== null,
  );
}

/**
 * Format a tariff direction code as a short translated suffix. BOT, and every
 * direction when nothing in the summary is an export, render blank.
 */
function directionSuffix(direction: string, showDirections: boolean): string {
  if (!showDirections || (direction !== 'IMP' && direction !== 'EXP')) return '';
  return ` (${codeLabel('summary_sections.directions', direction, direction)})`;
}

/** True when any selected tariff (retailer, grid or authority) is an export tariff. */
function hasExportTariff(data: Record<string, unknown>): boolean {
  const entries = [
    ...arr(obj(data, 'retailer'), 'tariffs'),
    ...arr(obj(data, 'dso'), 'tariffs'),
    ...arr(data, 'authorities'),
  ];
  return entries.some((entry) => str(entry, 'direction') === 'EXP');
}

/** Look up an option name by id in the cache, or fall back to the raw id. */
function optionName(cache: Map<StepId, StepOptionsResponse>, step: StepId, id: string): string {
  const options = cache.get(step);
  if (!options) return id;
  const match = options.options.find((o) => o.id === id);
  return match ? match.name : id;
}

/**
 * Resolve a human-readable display name from multiple sources:
 *   1. display_values (backend-provided, keyed by dvKey)
 *   2. optionsCache (option ID → name lookup)
 *   3. Raw ID (fallback)
 */
function displayName(
  dv: Record<string, string>,
  cache: Map<StepId, StepOptionsResponse>,
  step: StepId,
  id: string,
  dvKey: string,
): string {
  if (dv[dvKey]) return dv[dvKey];
  return optionName(cache, step, id);
}

/**
 * Resolve a tariff name from the backend's per-tariff display_values
 * (`tariff.<tariff_id>`), falling back to the relevant options cache, then
 * to the raw id. Direction suffixes are appended by callers via
 * `directionSuffix()` — never embedded in `display_values`.
 */
function resolveTariffName(
  dv: Record<string, string>,
  cache: Map<StepId, StepOptionsResponse>,
  cacheStep: StepId,
  tariffId: string,
): string {
  const dvValue = dv[`tariff.${tariffId}`];
  if (dvValue) return dvValue;
  return optionName(cache, cacheStep, tariffId);
}

/**
 * Extract rows for property_values using the options cache to resolve labels.
 * collected_data stores: { property_key: { ltp_id, condition_value } }
 * Options cache stores:  metadata.property_key, metadata.condition_value, opt.name
 */
function propertyRows(
  propValues: Record<string, unknown>,
  cache: Map<StepId, StepOptionsResponse>,
  cacheStep: StepId,
  autoSelected = false,
): Row[] {
  const rows: Row[] = [];
  const stepCache = cache.get(cacheStep);

  for (const [key, entry] of Object.entries(propValues)) {
    if (typeof entry !== 'object' || entry === null) continue;
    const cv = (entry as Record<string, unknown>)['condition_value'];
    if (typeof cv !== 'string') continue;

    const match = stepCache?.options.find(
      (o) => o.metadata['property_key'] === key && o.metadata['condition_value'] === cv,
    );

    const label = match
      ? (typeof match.metadata['question'] === 'string' ? match.metadata['question'] : key.replace(/_/g, ' '))
      : key.replace(/_/g, ' ');

    rows.push({ label, value: match ? match.name : cv, autoSelected });
  }
  return rows;
}

/** Resolve retailer logo URL from the options cache. */
function resolveRetailerLogo(
  cache: Map<StepId, StepOptionsResponse>,
  orgId: string,
): string | null {
  const orgOptions = cache.get('retailer_org');
  if (!orgOptions) return null;
  const match = orgOptions.options.find((o) => o.id === orgId);
  const url = match?.metadata['logo_url'];
  return isSafeImageUrl(url) ? url : null;
}

// ---------------------------------------------------------------------------
// Data structures
// ---------------------------------------------------------------------------

interface Row {
  label: string;
  value: string;
  /** True when the backend auto-resolved this value. */
  autoSelected?: boolean;
  /** Keep the label for screen readers only (repeats the row above, or the section title). */
  hideLabel?: boolean;
}

interface LocationData {
  countryCode: string;
  address: string;
  consumerType: string;
  editStepId?: StepId;
  isPredefined: boolean;
}

interface TariffSubsection {
  key: string;
  title: string;
  icon?: ReactNode;
  editStepId?: StepId;
  /** True when the backend auto-resolved the organisation. */
  orgAutoSelected: boolean;
  /** True when the backend auto-resolved the (leaf) tariff. */
  tariffAutoSelected: boolean;
  /** Primary line: org name \u00b7 tariff name */
  orgName: string;
  tariffName: string;
  /** Optional logo URL (retailer only). */
  logoUrl?: string | null;
  /** Extra detail rows (properties, signup date, custom prices). */
  detailRows: Row[];
  /** Optional sandbox notice shown under the headline (e.g. missing DSO tariffs). */
  notice?: string;
}

// ---------------------------------------------------------------------------
// Data builders
// ---------------------------------------------------------------------------

function buildLocationData(
  data: Record<string, unknown>,
  cache: Map<StepId, StepOptionsResponse>,
  predefined: Set<StepId>,
  nonEditable: Set<StepId>,
  dv: Record<string, string>,
): LocationData | null {
  const countryCode = str(data, 'country_code');
  const address = obj(data, 'address');
  const displayAddr = str(address, 'formatted_address') || str(address, 'display');
  const lctId = str(data, 'local_consumer_type_id');

  if (!countryCode && !displayAddr) return null;

  return {
    countryCode,
    address: displayAddr || displayName(dv, cache, 'country', countryCode, 'country'),
    consumerType: lctId
      ? displayName(dv, cache, 'local_consumer_type', lctId, 'local_consumer_type')
      : '',
    editStepId: nonEditable.has('country') ? undefined : 'country',
    isPredefined: predefined.has('country'),
  };
}

function buildTariffSubsections(
  data: Record<string, unknown>,
  cache: Map<StepId, StepOptionsResponse>,
  nonEditable: Set<StepId>,
  dv: Record<string, string>,
  autoSelected: Set<StepId>,
  sandboxNotices: string[],
): TariffSubsection[] {
  const subs: TariffSubsection[] = [];
  const ret = obj(data, 'retailer');
  const dso = obj(data, 'dso');
  const showDirections = hasExportTariff(data);

  // -- Energy Provider (Retailer) --
  const retOrgId = str(ret, 'organization_id');
  if (retOrgId) {
    const retTariffs = arr(ret, 'tariffs');
    const isCustom = ret['is_custom'] === true;
    const detailRows: Row[] = [];

    let tariffName = '';
    if (isCustom) {
      const custom = obj(ret, 'custom_tariff');
      const customType = str(custom, 'type');
      tariffName = customType
        ? t('summary_sections.customTariff', {
            type: customTypeLabel(
              customType,
              optionName(cache, 'retailer_custom_type', customType),
            ),
          })
        : '';

      // Custom price rows
      const priceInput = obj(custom, 'price_input');
      const pricesCache = cache.get('retailer_custom_prices');
      const pricesArr = Array.isArray(priceInput['prices'])
        ? (priceInput['prices'] as (Record<string, unknown> | null)[])
        : null;
      const componentsArr = Array.isArray(priceInput['components'])
        ? (priceInput['components'] as (Record<string, unknown> | null)[])
        : null;
      const singlePv = typeof priceInput['price_value'] === 'number'
        ? priceInput['price_value']
        : null;
      const vatSuffix = priceInput['vat_included'] === false
        ? ` (${t('steps.retailer_custom_prices.vatExcludedOption')})`
        : ` (${t('steps.retailer_custom_prices.vatIncludedOption')})`;

      if (pricesCache) {
        for (const opt of pricesCache.options) {
          const meta = opt.metadata;
          const hasFormula = meta['has_formula'] === true;
          const ptId = typeof meta['price_template_id'] === 'string'
            ? meta['price_template_id']
            : opt.id;
          const currCode = typeof meta['currency'] === 'string' ? meta['currency'] : '';
          const unitLabel = typeof meta['unit_label'] === 'string' ? meta['unit_label'] : '';
          const useSubunit = meta['display_in_subunit'] === true;
          const subunitName = typeof meta['subunit_name'] === 'string' ? meta['subunit_name'] : null;
          const subunitFactor = typeof meta['subunit_factor'] === 'number' ? meta['subunit_factor'] : 1;
          const currSymbol = useSubunit && subunitName
            ? subunitName
            : (CURRENCY_SYMBOLS[currCode] ?? currCode);
          const hint = [currSymbol, unitLabel].filter(Boolean).join(' ');
          const componentName = typeof meta['component_name'] === 'string' && meta['component_name']
            ? meta['component_name']
            : opt.name;

          if (hasFormula) {
            detailRows.push({ label: componentName, value: t('steps.retailer_custom_prices.formulaAuto') });
          } else {
            let rawValue: number | null = null;
            if (componentsArr) {
              const compId = typeof meta['component_template_id'] === 'string'
                ? meta['component_template_id']
                : null;
              const compEntry = compId
                ? componentsArr.find((c) => c?.['id'] === compId)
                : null;
              if (compEntry) {
                const compPrices = Array.isArray(compEntry['prices'])
                  ? (compEntry['prices'] as (Record<string, unknown> | null)[])
                  : null;
                if (compPrices) {
                  const entry = compPrices.find((p) => p?.['id'] === ptId);
                  if (entry && typeof entry['price_value'] === 'number') {
                    rawValue = entry['price_value'];
                  }
                }
                if (rawValue === null && typeof compEntry['price_value'] === 'number') {
                  rawValue = compEntry['price_value'];
                }
              }
            } else if (pricesArr) {
              const entry = pricesArr.find((p) => p?.['id'] === ptId);
              if (entry && typeof entry['price_value'] === 'number') rawValue = entry['price_value'];
            } else if (singlePv !== null) {
              rawValue = singlePv;
            }
            const display = rawValue !== null
              ? `${(rawValue * (useSubunit ? subunitFactor : 1)).toFixed(3)} ${hint}${vatSuffix}`.trim()
              : '\u2014';
            detailRows.push({ label: componentName, value: display });
          }
        }
      }
    } else if (retTariffs.length > 0) {
      tariffName = retTariffs
        .map((entry) => {
          const tid = str(entry, 'tariff_id');
          const dir = str(entry, 'direction') || 'BOT';
          const name = resolveTariffName(dv, cache, 'retailer_tariff', tid);
          return `${name}${directionSuffix(dir, showDirections)}`;
        })
        .join(', ');
    }

    const retPropValues = obj(ret, 'property_values');
    detailRows.push(
      ...propertyRows(
        retPropValues,
        cache,
        'retailer_properties',
        autoSelected.has('retailer_properties'),
      ),
    );
    const signupDate = str(ret, 'signup_date');
    if (signupDate) {
      detailRows.push({
        label: t('summary_sections.signupDate'),
        value: signupDate,
        autoSelected: autoSelected.has('retailer_signup_date'),
      });
    }

    const energyEditTarget: StepId = isCustom ? 'retailer_custom_prices' : 'retailer_org';
    subs.push({
      key: 'energy',
      title: t('summary_sections.energyProvider'),
      icon: getStepIcon('retailer_org'),
      editStepId: nonEditable.has(energyEditTarget) ? undefined : energyEditTarget,
      orgAutoSelected: autoSelected.has('retailer_org'),
      tariffAutoSelected: !isCustom && autoSelected.has('retailer_tariff'),
      orgName: displayName(dv, cache, 'retailer_org', retOrgId, 'retailer_org'),
      tariffName,
      logoUrl: resolveRetailerLogo(cache, retOrgId),
      detailRows,
    });
  }

  // -- Grid --
  // Built when a DSO was resolved OR when the notice needs a home: the backend
  // drops the whole DSO branch when no curated DSO covers the address, so the
  // notice would otherwise have no section to attach to.
  const dsoOrgId = str(dso, 'organization_id');
  const dsoNotice = sandboxNotices.includes('dso_tariffs_unavailable')
    ? t('summary_sections.dsoTariffsUnavailable')
    : undefined;

  if (dsoOrgId || dsoNotice) {
    const detailRows: Row[] = [];
    let dsoTariffName = '';

    if (dsoOrgId) {
      const dsoPropValues = obj(dso, 'property_values');
      detailRows.push(
        ...propertyRows(
          dsoPropValues,
          cache,
          'dso_properties',
          autoSelected.has('dso_properties'),
        ),
      );

      dsoTariffName = arr(dso, 'tariffs')
        .map((entry) => {
          const tid = str(entry, 'tariff_id');
          const dir = str(entry, 'direction') || 'BOT';
          const name = resolveTariffName(dv, cache, 'dso_tariff', tid);
          return `${name}${directionSuffix(dir, showDirections)}`;
        })
        .join(', ');
    }

    subs.push({
      key: 'dso',
      title: t('summary_sections.grid'),
      icon: getStepIcon('dso_org'),
      // No org means dso_org is not in the flow, so editing it would dead-end.
      editStepId:
        dsoOrgId && !nonEditable.has('dso_org') ? 'dso_org' : undefined,
      orgAutoSelected: autoSelected.has('dso_org'),
      tariffAutoSelected: autoSelected.has('dso_tariff'),
      orgName: dsoOrgId ? displayName(dv, cache, 'dso_org', dsoOrgId, 'dso_org') : '',
      tariffName: dsoTariffName,
      detailRows,
      notice: dsoNotice,
    });
  }

  // -- Authorities --
  // Grouped by type in the backend's order, the type label shown once per group,
  // and only when there is more than one type (the section title says the rest).
  const authEntries = arr(data, 'authorities');
  if (authEntries.length > 0) {
    const authCache = cache.get('authorities');
    const types = [...new Set(authEntries.map((entry) => str(entry, 'authority_type')))];
    const grouped = types.flatMap((type) =>
      authEntries.filter((entry) => str(entry, 'authority_type') === type),
    );
    const detailRows: Row[] = grouped.map((entry, index) => {
      const tid = str(entry, 'tariff_id');
      const atype = str(entry, 'authority_type');
      const dir = str(entry, 'direction') || 'BOT';
      const baseName = resolveTariffName(dv, cache, 'authorities', tid);
      const match = authCache?.options.find((o) => o.id === tid);
      const startsGroup =
        index === 0 || str(grouped[index - 1] ?? {}, 'authority_type') !== atype;
      // A row is auto-selected when its option is non-removable (i.e.
      // assignment === 'mandatory'). When the entire step was auto-skipped
      // by the backend, every option is mandatory so every row gets a badge.
      return {
        label: codeLabel('summary_sections.authorityTypes', atype, atype.toUpperCase()),
        hideLabel: types.length < 2 || !startsGroup,
        value: `${baseName}${directionSuffix(dir, showDirections)}`,
        autoSelected: match ? !getRemovable(match) : false,
      };
    });

    const hadChoice = authCache?.options.some(getRemovable) ?? false;

    subs.push({
      key: 'authorities',
      title: t('summary_sections.authorities'),
      icon: getStepIcon('authorities'),
      editStepId: hadChoice && !nonEditable.has('authorities') ? 'authorities' : undefined,
      orgAutoSelected: false,
      tariffAutoSelected: false,
      orgName: '',
      tariffName: '',
      detailRows,
    });
  }

  return subs;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SummaryStep = memo(function SummaryStep({ onSubmit, isSubmitting }: StepProps) {
  const { session, optionsCache, error } = useSessionState();
  const { navigateToStep } = useSessionActions();
  const predefined = useMemo(
    () => getPredefinedSet(session?.predefined_steps),
    [session?.predefined_steps],
  );
  const nonEditable = useMemo<Set<StepId>>(
    () => new Set(session?.non_editable_steps ?? []),
    [session?.non_editable_steps],
  );
  // Steps the backend auto-resolved (in non_editable_steps but not predefined).
  const autoSelected = useMemo<Set<StepId>>(
    () => new Set(
      (session?.non_editable_steps ?? []).filter((s) => !predefined.has(s)),
    ),
    [session?.non_editable_steps, predefined],
  );

  usePrefetchSummaryOptions();

  const dv = session?.display_values ?? {};
  const sandboxNotices = useMemo(
    () => session?.sandbox_notices ?? [],
    [session?.sandbox_notices],
  );

  const location = useMemo(() => {
    const data = session?.collected_data ?? {};
    return buildLocationData(data, optionsCache, predefined, nonEditable, dv);
  }, [session?.collected_data, optionsCache, predefined, nonEditable, dv]);

  const tariffSubs = useMemo(() => {
    const data = session?.collected_data ?? {};
    return buildTariffSubsections(data, optionsCache, nonEditable, dv, autoSelected, sandboxNotices);
  }, [session?.collected_data, optionsCache, nonEditable, dv, autoSelected, sandboxNotices]);

  const handleEdit = useCallback(
    (step: StepId) => {
      void navigateToStep(step);
    },
    [navigateToStep],
  );

  const handleConfirm = useCallback(() => {
    onSubmit({ confirmed: true });
  }, [onSubmit]);

  return (
    <>
      <StepHeading title={t('steps.summary.title')} subtitle={t('steps.summary.subtitle')} icon={getStepIcon('summary')} />

      {error && (
        <div className={styles['error-banner']} role="alert">
          <p>{error.message || t('error.fallback')}</p>
        </div>
      )}

      <div className={styles['sections']}>
        {/* ── Location card ── */}
        {location && (
          <section className={styles['card']}>
            <div className={styles['card-header']}>
              <span className={styles['card-title-group']}>
                <span className={styles['card-icon']}>{getStepIcon('address')}</span>
                <h3 className={styles['card-title']}>{t('summary_sections.location')}</h3>
              </span>
              {location.editStepId && (
                <button
                  type="button"
                  className={styles['edit-button']}
                  onClick={() => {
                    if (location.editStepId) handleEdit(location.editStepId);
                  }}
                  disabled={isSubmitting}
                  aria-label={t('common.editAriaLabel', { section: t('summary_sections.location') })}
                >
                  {t('common.edit')}
                </button>
              )}
              {!location.editStepId && location.isPredefined && (
                <span className={styles['predefined-badge']}>
                  {t('predefined.badge')}
                </span>
              )}
            </div>
            <div className={styles['location-body']}>
              <div className={styles['location-address']}>
                {location.countryCode && (
                  <img
                    src={countryFlagUrl(location.countryCode)}
                    alt={location.countryCode}
                    className={styles['location-flag']}
                  />
                )}
                <span className={styles['value']}>{location.address}</span>
              </div>
              {location.consumerType && (
                <span className={styles['location-consumer']}>{location.consumerType}</span>
              )}
            </div>
          </section>
        )}

        {/* ── Tariffs card (merged subsections) ── */}
        {tariffSubs.length > 0 && (
          <section className={styles['card']}>
            <div className={styles['card-header']}>
              <h3 className={styles['card-title']}>{t('summary_sections.tariffs')}</h3>
            </div>

            {tariffSubs.map((sub, idx) => (
              <div key={sub.key}>
                {idx > 0 && <hr className={styles['subsection-divider']} />}
                <div className={styles['subsection']}>
                  <div className={styles['subsection-header']}>
                    <span className={styles['card-title-group']}>
                      {sub.icon && <span className={styles['card-icon']}>{sub.icon}</span>}
                      <h4 className={styles['subsection-title']}>{sub.title}</h4>
                    </span>
                    {sub.editStepId && (
                      <button
                        type="button"
                        className={styles['edit-button']}
                        onClick={() => {
                          if (sub.editStepId) handleEdit(sub.editStepId);
                        }}
                        disabled={isSubmitting}
                        aria-label={t('common.editAriaLabel', { section: sub.title })}
                      >
                        {t('common.edit')}
                      </button>
                    )}
                  </div>

                  {/* Headline: logo + org · tariff */}
                  {sub.orgName && (
                    <div className={styles['headline']}>
                      {sub.logoUrl && (
                        <img
                          src={sub.logoUrl}
                          alt=""
                          className={styles['headline-logo']}
                        />
                      )}
                      <div className={styles['headline-names']}>
                        <span className={styles['headline-org']}>
                          {sub.orgName}
                          {sub.orgAutoSelected && (
                            <span className={styles['auto-pill']}>
                              {t('steps.summary.autoBadgeShort')}
                            </span>
                          )}
                        </span>
                        {sub.tariffName && (
                          <span className={styles['headline-tariff']}>
                            {sub.tariffName}
                            {sub.tariffAutoSelected && (
                              <span className={styles['auto-pill']}>
                                {t('steps.summary.autoBadgeShort')}
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Sandbox notice (e.g. DSO tariffs unavailable) */}
                  {sub.notice && (
                    <p className={styles['notice']} role="note">
                      <svg
                        className={styles['notice-icon']}
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                        <line x1="12" x2="12" y1="9" y2="13" />
                        <line x1="12" x2="12.01" y1="17" y2="17" />
                      </svg>
                      <span>{sub.notice}</span>
                    </p>
                  )}

                  {/* Detail rows (properties, prices, signup date, authority tariffs) */}
                  {sub.detailRows.length > 0 && (
                    <dl className={styles['detail-rows']}>
                      {sub.detailRows.map((row, index) => (
                        <div key={`${row.label}:${index}`} className={styles['row']}>
                          <dt className={row.hideLabel ? 'po-sr-only' : styles['label']}>
                            {row.label}
                          </dt>
                          <dd className={styles['value']}>
                            {row.value}
                            {row.autoSelected && (
                              <span className={styles['auto-pill']}>
                                {t('steps.summary.autoBadgeShort')}
                              </span>
                            )}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              </div>
            ))}
          </section>
        )}
      </div>

      <FooterActions
        primaryLabel={error ? t('common.retry') : t('common.confirm')}
        onPrimary={handleConfirm}
        isLoading={isSubmitting}
      />
    </>
  );
});
